import { displaySettingsMessages, rateLabel } from '@/i18n/display-settings'
import { displayRate, formatRate, type RateUnit } from '@/core/display-settings'
import type { RealtimeExpRateMetric } from '@/domain/types'
import type { AppLocale } from '@/i18n/messages'
import { DigitRollAnimator } from './digit-roll'

export interface ExpRateTrendPresentation {
  direction: 'up' | 'down' | null
  text: string
  description: string
  magnitude?: number
  /** Raw comparison sign while reliable, even when below the visibility threshold. */
  comparisonDirection?: 'up' | 'down' | null
}

export function formatExpRateTrend(metric: RealtimeExpRateMetric | null | undefined, average: number | null | undefined,
  locale: AppLocale, running = true, rateUnit: RateUnit = 'hour', maximumFractionDigits?: number): ExpRateTrendPresentation {
  const format = (value: number | null | undefined) => value == null || !Number.isFinite(value) ? '—'
    : formatRate(value, locale, rateUnit, { maximumFractionDigits: maximumFractionDigits ?? 2 })
  const ready = running && metric?.status === 'ready' && metric.value != null && metric.delta != null
  const magnitude = ready ? Math.abs(metric.delta!) : 0
  const text = (displayRate(magnitude, rateUnit) ?? 0) < 10_000
    ? formatRate(magnitude, locale, rateUnit, { notation: 'standard', maximumFractionDigits: maximumFractionDigits ?? (rateUnit === 'minute' ? 2 : 0), useGrouping: false })
    : format(magnitude)
  const direction = ready && magnitude >= 1 ? metric.direction : null
  const time = metric?.updatedAt == null ? '—' : Intl.DateTimeFormat(locale, {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(metric.updatedAt)
  const comparison = ready ? `${metric.delta! > 0 ? '+' : metric.delta! < 0 ? '−' : ''}${text}` : '—'
  const values: Record<string, string> = { window: String((metric?.windowMs ?? 60_000) / 1000), p0: ready ? format(metric.value) : '—', p1: format(average), p2: comparison, p3: time, unit: rateLabel(locale, rateUnit) }
  const description = displaySettingsMessages[locale].trendDescription.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match)
  return { direction, text: direction ? text : '', description, magnitude: ready ? magnitude : undefined,
    comparisonDirection: ready ? metric.delta! > 0 ? 'up' : metric.delta! < 0 ? 'down' : null : undefined }
}

const SVG_NS = 'http://www.w3.org/2000/svg'
const EASING = 'cubic-bezier(.2,.8,.2,1)'
export const EXP_TREND_HOLD_MS = 1_500
export const EXP_TREND_HIDE_DELAY_MS = 1_500

/** Shared by Vue and the native PiP renderer. Semantic text always holds the latest result. */
export class ExpRateTrendAnimator {
  private layer: HTMLSpanElement
  private number: HTMLSpanElement
  private arrow: SVGSVGElement
  private animations = new Map<Element, Animation>()
  private digits = new DigitRollAnimator()
  private slots = new Map<string, HTMLSpanElement>()
  private key = ''
  private context = ''
  private visible = false
  private magnitude: number | undefined
  private unit = ''
  private presentedAt = -Infinity
  private pending: { model: ExpRateTrendPresentation; context: string } | undefined
  private timer: number | undefined
  private hideTimer: number | undefined
  private pendingHide: ExpRateTrendPresentation | undefined

  constructor(private readonly root: HTMLElement, private readonly onPresent?: (model: ExpRateTrendPresentation | null | undefined) => void) {
    root.classList.add('exp-rate-trend')
    root.dataset.visible = 'false'
    this.layer = root.ownerDocument.createElement('span')
    this.layer.className = 'exp-rate-trend-layer'
    this.layer.setAttribute('aria-hidden', 'true')
    this.number = root.ownerDocument.createElement('span')
    this.number.className = 'exp-trend-number'
    this.arrow = root.ownerDocument.createElementNS(SVG_NS, 'svg')
    this.arrow.setAttribute('viewBox', '0 0 24 24')
    this.arrow.setAttribute('fill', 'currentColor')
    this.arrow.setAttribute('aria-hidden', 'true')
    const path = root.ownerDocument.createElementNS(SVG_NS, 'path')
    path.setAttribute('d', 'M12 2 3 11h18ZM12 12 3 21h18Z')
    this.arrow.append(path)
    this.layer.append(this.arrow, this.number)
    root.append(this.layer)
  }

  update(model: ExpRateTrendPresentation | null | undefined, context: string, animate = true): boolean {
    const owner = this.root.ownerDocument.defaultView
    if (owner && animate && this.visible && context === this.context && model && !model.direction
      && (model.comparisonDirection === null || model.comparisonDirection === this.root.dataset.direction)) {
      this.clearPending()
      this.pendingHide = model
      // Start once: repeated below-threshold samples must not extend the deadline.
      if (this.hideTimer === undefined) this.hideTimer = owner.setTimeout(() => {
        const latest = this.pendingHide
        this.hideTimer = undefined
        this.pendingHide = undefined
        if (latest && this.render(latest, context, true)) this.presentedAt = Date.now()
      }, EXP_TREND_HIDE_DELAY_MS)
      return false
    }
    // Recovery cancels the delayed hide; invalid data, reversal and session changes
    // bypass it so an obsolete arrow is never held in the wrong direction.
    this.clearPendingHide()
    const key = `${model?.direction ?? null}:${model?.text ?? ''}`
    const remaining = EXP_TREND_HOLD_MS - (Date.now() - this.presentedAt)
    if (owner && animate && model?.direction && model.direction === this.root.dataset.direction
      && context === this.context && key !== this.key && remaining > 0) {
      // One trailing update always holds the newest value; no animation queue.
      this.pending = { model, context }
      if (this.timer === undefined) this.timer = owner.setTimeout(() => {
        const latest = this.pending
        this.timer = undefined
        this.pending = undefined
        if (latest && this.render(latest.model, latest.context, true)) this.presentedAt = Date.now()
      }, remaining)
      return false
    }
    this.clearPending()
    const changed = this.render(model, context, animate)
    if (changed) this.presentedAt = Date.now()
    return changed
  }

  private render(model: ExpRateTrendPresentation | null | undefined, context: string, animate: boolean): boolean {
    const direction = model?.direction ?? null
    const visible = direction !== null
    const key = `${direction}:${model?.text ?? ''}`
    this.root.setAttribute('aria-label', model?.description ?? '')
    this.root.setAttribute('aria-hidden', String(!visible))
    this.root.title = model?.description ?? ''
    this.onPresent?.(model)
    const changedContext = this.context !== context
    this.context = context
    if (key === this.key && !changedContext) {
      if (!animate) { this.settle(); if (!visible) this.clearNumber() }
      this.magnitude = model?.magnitude
      return false
    }
    this.key = key
    const previousVisible = this.visible
    const previousDirection = this.root.dataset.direction
    const style = this.style(this.layer)
    const canAnimate = animate && !changedContext && Boolean(this.layer.animate)
    if (!canAnimate) this.cancel()
    this.visible = visible
    this.root.dataset.visible = String(visible)
    this.root.dataset.direction = direction ?? ''
    this.root.dataset.text = visible ? model!.text : ''
    if (!visible) {
      // Stop digit motion immediately; only the visibility fade may remain.
      this.settle()
      if (canAnimate && previousVisible) {
        this.play(this.layer, [{ opacity: style.opacity }, { opacity: 0 }], 180, () => this.clearNumber())
      } else this.clearNumber()
      this.magnitude = undefined
      return true
    }
    if (changedContext || !previousVisible) this.clearNumber()
    this.layer.dataset.direction = direction!
    const arrowStyle = this.style(this.arrow)
    this.arrow.querySelector('path')!.setAttribute('d', direction === 'up'
      ? 'M12 2 3 11h18ZM12 12 3 21h18Z'
      : 'M3 3h18l-9 9ZM3 13h18l-9 9Z')
    this.arrow.style.transform = 'translateY(0)'
    // Only an appearance moves the arrow. Value updates keep it stationary;
    // a direction change also cancels any unfinished entrance in the old direction.
    if (previousVisible && previousDirection !== direction) this.stop(this.arrow)
    if (canAnimate && !previousVisible) this.play(this.arrow, [
      { transform: arrowStyle.transform, opacity: arrowStyle.opacity, offset: 0, easing: 'ease-in' },
      { transform: `translateY(${direction === 'up' ? '-' : ''}.85em)`, opacity: 0, offset: .42 },
      { transform: `translateY(${direction === 'up' ? '' : '-'}.85em)`, opacity: 0, offset: .43, easing: EASING },
      { transform: 'translateY(0)', opacity: 1, offset: 1 },
    ], 520, undefined, 'linear')
    if (!previousVisible || previousDirection !== direction || !canAnimate) {
      // Cancel the old fade before sampling the new CSS color.
      this.stop(this.layer)
      const color = this.style(this.layer).color
      if (canAnimate) this.play(this.layer,
        [{ opacity: previousVisible ? style.opacity : 0, color: style.color }, { opacity: 1, color }], previousVisible ? 200 : 180)
    }
    const match = /^([\d,]+)(?:\.(\d+))?(.*)$/.exec(model!.text)
    const unit = match?.[3] ?? model!.text
    const roll = canAnimate && previousVisible && unit === this.unit
      && Number.isFinite(this.magnitude) && Number.isFinite(model?.magnitude)
    const sign = (model?.magnitude ?? 0) >= (this.magnitude ?? 0) ? -1 : 1
    if (!roll) {
      const opacity = this.style(this.number).opacity
      this.clearNumber()
      if (canAnimate && previousVisible) this.play(this.number, [{ opacity: Math.min(Number(opacity), .35) }, { opacity: 1 }], 180)
    }
    const tokens: { key: string; text: string; digit: boolean }[] = []
    if (match) {
      const integer = match[1]!.replaceAll(',', '')
      Array.from(integer).forEach((text, i) => tokens.push({ key: `integer-${integer.length - i - 1}`, text, digit: true }))
      if (match[2]) {
        tokens.push({ key: 'decimal', text: '.', digit: false })
        Array.from(match[2]).forEach((text, i) => tokens.push({ key: `fraction-${i}`, text, digit: true }))
      }
    }
    tokens.push({ key: 'unit', text: unit, digit: false })
    const retained = new Set(tokens.map(token => token.key))
    for (const [slotKey, slot] of this.slots) if (!retained.has(slotKey)) {
      this.digits.stopTree(slot); slot.remove(); this.slots.delete(slotKey)
    }
    for (const token of tokens) {
      let slot = this.slots.get(token.key)
      if (!slot) {
        slot = this.root.ownerDocument.createElement('span')
        slot.className = token.digit ? 'exp-trend-digit' : 'exp-trend-literal'
        slot.dataset.place = token.key
        this.slots.set(token.key, slot)
      }
      if (slot.dataset.target !== token.text) {
        if (token.digit) this.digits.update(slot, token.text, roll && slot.dataset.target !== undefined, sign)
        else slot.textContent = token.text
        slot.dataset.target = token.text
      }
      this.number.append(slot)
    }
    this.unit = unit
    this.magnitude = model?.magnitude
    if (!canAnimate) this.settle()
    return true
  }

  private style(node: Element): { transform: string; opacity: string; color: string } {
    const css = this.root.ownerDocument.defaultView?.getComputedStyle(node)
    return { transform: css?.transform || 'none', opacity: css?.opacity || '1', color: css?.color || 'currentColor' }
  }

  private play(node: HTMLElement | SVGElement, frames: Keyframe[], duration: number, done?: () => void, easing = EASING): void {
    this.stop(node)
    if (!node.animate) { done?.(); return }
    const animation = node.animate(frames, { duration, easing })
    this.animations.set(node, animation)
    animation.onfinish = () => {
      if (this.animations.get(node) !== animation) return
      this.animations.delete(node)
      done?.()
    }
  }

  private stop(node: Element): void {
    const animation = this.animations.get(node)
    if (animation) { animation.onfinish = null; animation.cancel(); this.animations.delete(node) }
  }

  private stopTree(node: Element): void {
    for (const target of this.animations.keys()) if (node.contains(target)) this.stop(target)
  }

  private cancel(): void {
    this.digits.cancel()
    for (const node of this.animations.keys()) this.stop(node)
  }

  private settle(): void {
    this.cancel()
    this.digits.settle(this.slots.values())
  }

  private clearNumber(): void {
    this.digits.cancel()
    this.stopTree(this.number)
    this.number.replaceChildren()
    this.slots.clear()
  }

  private clearPending(): void {
    if (this.timer !== undefined) this.root.ownerDocument.defaultView?.clearTimeout(this.timer)
    this.timer = undefined
    this.pending = undefined
  }

  private clearPendingHide(): void {
    if (this.hideTimer !== undefined) this.root.ownerDocument.defaultView?.clearTimeout(this.hideTimer)
    this.hideTimer = undefined
    this.pendingHide = undefined
  }

  destroy(): void {
    this.clearPendingHide()
    this.clearPending()
    this.cancel()
    this.root.replaceChildren()
  }
}
